import { useEffect, useState } from "react"
import { InputNumber } from "antd"
import { ButtonPrimary } from "components/ui/buttons/ButtonPrimary/ButtonPrimary"
import { ButtonSecondary } from "components/ui/buttons/ButtonSecondary/ButtonSecondary"
import { CloseIcon } from "components/ui/icons/close/CloseIcon"
import { Input } from "components/ui/inputs/Input"
import { Modal } from "components/ui/modals/Modal/Modal"
import { SegmentedWithoutBg } from "components/ui/segmented/SegmentedWithoutBg/SegmentedWithoutBg"
import { SelectBaseModelNumberModal } from "./SelectBaseModelNumberModal"
import type { TBaseModelNumber } from "./SelectBaseModelNumberModal"
import type { TCustomModelNumber, TCustomModelNumberPayload } from "types/customModelNumbers.type"
import style from "./CustomModelNumberModal.module.scss"

const {
	modal,
	header,
	title,
	subtitle,
	closeButton,
	modeSelector,
	form,
	field,
	label,
	requiredMark,
	fieldRow,
	dimensionsRow,
	modelNumberPrefix,
	baseModelControl,
	baseModelLink,
	baseModelChip,
	removeBaseModel,
	footer,
	footerButtons,
} = style

type TCreationMode = "from-scratch" | "based-on-existing"

type Props = {
	customModelNumber: TCustomModelNumber | null
	isOpen: boolean
	isPending: boolean
	onClose: () => void
	onSave: (payload: TCustomModelNumberPayload) => void
	/**
	 * Where this modal (and its nested base-model picker) portals to. Omitted on the management
	 * screen (antd's default, `document.body`, is correct there); the quotation editor passes its own
	 * shadow-DOM container — both the "add custom object" flow (#1371) and the model number picker's
	 * Custom tab (#1346) — since that page lives inside the Apryse shadow root.
	 */
	getContainer?: () => HTMLElement
}

const getInitialSuffix = (customModelNumber: TCustomModelNumber): string => {
	const baseModelNumber = customModelNumber.derived_from?.model_number
	if (baseModelNumber == null || !customModelNumber.model_number.startsWith(baseModelNumber)) {
		return customModelNumber.model_number
	}

	return customModelNumber.model_number.slice(baseModelNumber.length)
}

export const CustomModelNumberModal = ({
	customModelNumber,
	isOpen,
	isPending,
	onClose,
	onSave,
	getContainer,
}: Props) => {
	const isEditing = customModelNumber !== null
	const initialMode: TCreationMode = customModelNumber?.derived_from ? "based-on-existing" : "from-scratch"
	const [creationMode, setCreationMode] = useState<TCreationMode>(initialMode)
	const [modelNumber, setModelNumber] = useState("")
	const [modelNumberSuffix, setModelNumberSuffix] = useState("")
	const [name, setName] = useState("")
	const [price, setPrice] = useState<number | null>(null)
	const [width, setWidth] = useState<number | null>(null)
	const [height, setHeight] = useState<number | null>(null)
	const [depth, setDepth] = useState<number | null>(null)
	const [selectedBaseModel, setSelectedBaseModel] = useState<TBaseModelNumber | null>(null)
	const [isBaseModelModalOpen, setBaseModelModalOpen] = useState(false)
	const [showValidation, setShowValidation] = useState(false)

	const isDerivedMode = (isEditing ? initialMode : creationMode) === "based-on-existing"

	useEffect(() => {
		if (!isOpen) return

		setCreationMode(initialMode)
		setModelNumber(customModelNumber?.model_number ?? "")
		setModelNumberSuffix(customModelNumber ? getInitialSuffix(customModelNumber) : "")
		setName(customModelNumber?.name ?? "")
		setPrice(customModelNumber ? Number(customModelNumber.price) : null)
		setWidth(customModelNumber?.width ?? null)
		setHeight(customModelNumber?.height ?? null)
		setDepth(customModelNumber?.depth ?? null)
		setSelectedBaseModel(customModelNumber?.derived_from ?? null)
		setBaseModelModalOpen(false)
		setShowValidation(false)
	}, [customModelNumber, initialMode, isOpen])

	const finalModelNumber = isDerivedMode
		? `${selectedBaseModel?.model_number ?? ""}${modelNumberSuffix}`.trim()
		: modelNumber.trim()
	const dimensionsAreValid = width !== null && width > 0 && height !== null && height > 0 && depth !== null && depth > 0
	const isValid =
		finalModelNumber.length > 0 &&
		name.trim().length > 0 &&
		price !== null &&
		price >= 0 &&
		dimensionsAreValid &&
		(!isDerivedMode || (selectedBaseModel !== null && modelNumberSuffix.trim().length > 0))

	const save = () => {
		setShowValidation(true)
		if (!isValid || price === null || width === null || height === null || depth === null) return

		onSave({
			model_number: finalModelNumber,
			name: name.trim(),
			price: String(price),
			width,
			height,
			depth,
			...(isDerivedMode ? { derived_from_id: selectedBaseModel?.id } : { derived_from_id: null }),
		})
	}

	const hasInvalidModelNumber = finalModelNumber.length === 0 || (isDerivedMode && modelNumberSuffix.trim().length === 0)
	const selectBaseModel = (model: TBaseModelNumber) => {
		setSelectedBaseModel(model)
		setBaseModelModalOpen(false)
	}

	return (
		<Modal
			className={modal}
			isModalOpen={isOpen}
			setModalClose={onClose}
			width={600}
			closable={false}
			maskClosable={!isPending}
			getContainer={getContainer}
			styles={{ content: { padding: 0 }, body: { padding: 0 } }}
		>
			<div className={header}>
				<div>
					<h2 className={title}>{isEditing ? "Edit custom model number" : "Create custom model number"}</h2>
					<p className={subtitle}>
						{isEditing
							? "Changes will apply to this model number across all quotations in this drawing"
							: "Save it in this drawing and reuse it in its other quotations."}
					</p>
				</div>
				<button className={closeButton} type="button" onClick={onClose} disabled={isPending} aria-label="Close modal">
					<CloseIcon />
				</button>
			</div>
			{!isEditing ? (
				<SegmentedWithoutBg
					className={modeSelector}
					value={creationMode}
					onChange={(value) => setCreationMode(value as TCreationMode)}
					options={[
						{ label: "From scratch", value: "from-scratch" },
						{ label: "Based on existing", value: "based-on-existing" },
					]}
				/>
			) : null}
			<div className={form}>
				{isDerivedMode ? (
					<div className={field}>
						<span className={label}>
							Base model number <span className={requiredMark}>*</span>
						</span>
						<div className={baseModelControl}>
							{selectedBaseModel !== null ? (
								<span className={baseModelChip}>
									{selectedBaseModel.model_number}
									{/* Per design: the "x" re-opens the picker to choose a different base model — it does not clear it. */}
									<button
										className={removeBaseModel}
										type="button"
										onClick={() => setBaseModelModalOpen(true)}
										aria-label="Change base model number"
									>
										<CloseIcon size={10} />
									</button>
								</span>
							) : (
								<button className={baseModelLink} type="button" onClick={() => setBaseModelModalOpen(true)}>
									Select base model number
								</button>
							)}
						</div>
					</div>
				) : null}
				<div className={field}>
					<label className={label} htmlFor="custom-model-number">
						Model number <span className={requiredMark}>*</span>
					</label>
					{isDerivedMode && selectedBaseModel !== null ? (
						<div className={modelNumberPrefix}>
							<span>{selectedBaseModel.model_number}</span>
							<Input
								value={modelNumberSuffix}
								status={showValidation && hasInvalidModelNumber ? "error" : undefined}
								onChange={(event) => setModelNumberSuffix(event.target.value)}
							/>
						</div>
					) : isDerivedMode ? (
						<Input disabled placeholder="Select base model number first" status={showValidation ? "error" : undefined} />
					) : (
						<Input
							value={modelNumber}
							placeholder="e.g. 10370-X1"
							status={showValidation && hasInvalidModelNumber ? "error" : undefined}
							onChange={(event) => setModelNumber(event.target.value)}
						/>
					)}
				</div>
				<div className={fieldRow}>
					<div className={field}>
						<label className={label} htmlFor="custom-model-name">
							Object name <span className={requiredMark}>*</span>
						</label>
						<Input
							value={name}
							placeholder="e.g. Wall cabinet - shortened"
							status={showValidation && name.trim().length === 0 ? "error" : undefined}
							onChange={(event) => setName(event.target.value)}
						/>
					</div>
					<div className={field}>
						<label className={label} htmlFor="custom-model-price">
							Price <span className={requiredMark}>*</span>
						</label>
						<InputNumber
							id="custom-model-price"
							value={price}
							prefix="$"
							controls={false}
							min={0}
							status={showValidation && (price === null || price < 0) ? "error" : undefined}
							onChange={(value) => setPrice(typeof value === "number" ? value : null)}
						/>
					</div>
				</div>
				<div className={dimensionsRow}>
					{[
						{ id: "width", label: "Width", value: width, setValue: setWidth },
						{ id: "height", label: "Height", value: height, setValue: setHeight },
						{ id: "depth", label: "Depth", value: depth, setValue: setDepth },
					].map((dimension) => (
						<div className={field} key={dimension.id}>
							<label className={label} htmlFor={`custom-model-${dimension.id}`}>
								{dimension.label} <span className={requiredMark}>*</span>
							</label>
							<InputNumber
								id={`custom-model-${dimension.id}`}
								value={dimension.value}
								suffix="in"
								controls={false}
								min={1}
								status={showValidation && (dimension.value === null || dimension.value <= 0) ? "error" : undefined}
								onChange={(value) => dimension.setValue(typeof value === "number" ? value : null)}
							/>
						</div>
					))}
				</div>
			</div>
			<div className={footer}>
				<div className={footerButtons}>
					<ButtonSecondary type="button" onClick={onClose} submittable={!isPending}>
						Cancel
					</ButtonSecondary>
					<ButtonPrimary onClick={save} submittable={!isPending}>
						Save
					</ButtonPrimary>
				</div>
			</div>
			<SelectBaseModelNumberModal
				isOpen={isBaseModelModalOpen}
				initialSelected={selectedBaseModel}
				onClose={() => setBaseModelModalOpen(false)}
				onBack={() => setBaseModelModalOpen(false)}
				onSave={selectBaseModel}
				getContainer={getContainer}
			/>
		</Modal>
	)
}
